import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

export default function ResultView({ result }) {
  const MARKETPLACE_URL = "https://madanrg.flycom.shop/";

  return (
    <div className="min-h-screen bg-slate-950 py-12 px-4 flex items-center justify-center">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }} 
        animate={{ opacity: 1, scale: 1 }} 
        className="max-w-4xl w-full space-y-8"
      >
        {/* Main Recommendation */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-950 rounded-[3rem] p-8 md:p-12 border border-emerald-500/20 shadow-2xl relative overflow-hidden text-center">
          <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
            <SafeIcon icon="Zap" className="w-64 h-64 text-emerald-400" />
          </div>
          
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-black uppercase tracking-[0.2em] mb-8">
              <SafeIcon icon="Target" className="w-4 h-4" />
              Expert System Configuration ✨
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
              {result.systemType}
            </h1>
            
            <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10">
              {result.why}
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              <Metric icon="Sun" label="Peak Sun Hours" value={`${result.peakSunHours} hrs`} />
              <Metric icon="MapPin" label="Location" value={result.city} />
              <Metric icon="TrendingDown" label="ROI Estimate" value={result.savings} />
              <Metric icon="Activity" label="Daily Load" value={`${(result.metrics.dailyConsumption / 1000).toFixed(1)} kWh`} />
            </div>
          </div>
        </div>

        {/* Technical Specs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-slate-900/50 backdrop-blur-xl rounded-[2.5rem] p-8 md:p-10 border border-white/5">
            <h3 className="text-xl font-bold text-white mb-8 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                <SafeIcon icon="Settings" className="w-6 h-6" />
              </div>
              System Sizing (Installer-Grade)
            </h3>
            
            <div className="space-y-6">
              <SpecRow label="Recommended Solar" value={`${result.metrics.solarSize}W Array`} icon="Sun" desc="Total PV capacity needed" />
              <SpecRow label="Battery Capacity" value={`${(result.metrics.batterySize / 1000).toFixed(1)} kWh`} icon="BatteryCharging" desc="LiFePO4 storage (90% DoD)" />
              <SpecRow label="Inverter Size" value={`${result.metrics.inverterSize}W`} icon="Cpu" desc="Suggested pure sine wave" />
            </div>
          </div>

          <div className="bg-slate-900/50 backdrop-blur-xl rounded-[2.5rem] p-8 md:p-10 border border-white/5 flex flex-col justify-between">
            <div>
              <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                  <SafeIcon icon="DollarSign" className="w-6 h-6" />
                </div>
                Estimated Investment
              </h3>
              <p className="text-slate-500 text-sm mb-8 ml-13">Equipment + Professional Installation</p>
              
              <div className="bg-slate-950/50 p-8 rounded-3xl border border-white/5 text-center">
                <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-2">Total Project Cost</p>
                <div className="text-4xl md:text-5xl font-black text-white">
                  {result.costRange.symbol}{result.costRange.min.toLocaleString()} – {result.costRange.symbol}{result.costRange.max.toLocaleString()}
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-start gap-3 p-4 bg-amber-500/5 rounded-2xl border border-amber-500/10">
              <SafeIcon icon="AlertCircle" className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <p className="text-[11px] text-amber-500/80 leading-tight">
                This estimate uses a conservative 0.8 system loss factor and 1-day backup autonomy. Final pricing depends on site survey.
              </p>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="bg-emerald-500 rounded-[3rem] p-10 md:p-16 text-center shadow-2xl shadow-emerald-500/20">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">Ready to take control?</h2>
          <p className="text-emerald-50/90 text-sm md:text-base mb-2 max-w-xl mx-auto font-bold uppercase tracking-wide">
            Browse verified equipment and connect with trusted installers on our marketplace.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center mt-10">
            <a href={MARKETPLACE_URL} className="flex-1 sm:flex-none px-10 py-5 bg-white text-emerald-600 rounded-2xl font-extrabold text-xl hover:bg-emerald-50 transition-all flex items-center justify-center gap-3">
              Buy Equipment <SafeIcon icon="ShoppingCart" className="w-6 h-6" />
            </a>
            <a href={MARKETPLACE_URL} className="flex-1 sm:flex-none px-10 py-5 bg-emerald-600 text-white rounded-2xl font-extrabold text-xl hover:bg-emerald-700 transition-all border border-emerald-400 flex items-center justify-center gap-3">
              Contact Installer <SafeIcon icon="UserCheck" className="w-6 h-6" />
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Metric({ icon, label, value }) {
  return (
    <div className="p-3 rounded-2xl bg-white/5 border border-white/5">
      <SafeIcon icon={icon} className="w-4 h-4 text-emerald-400 mb-1.5 mx-auto" />
      <p className="text-[9px] text-slate-500 uppercase font-black tracking-widest mb-0.5">{label}</p>
      <p className="text-white font-bold text-xs truncate">{value}</p>
    </div>
  );
}

function SpecRow({ label, value, icon, desc }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 flex-shrink-0">
        <SafeIcon icon={icon} className="w-6 h-6" />
      </div>
      <div>
        <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">{label}</p>
        <p className="text-white font-bold text-lg">{value}</p>
        <p className="text-[10px] text-slate-600">{desc}</p>
      </div>
    </div>
  );
}